import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';

import {
  ExternalLink,
  IconArrowHead,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  NamedLink,
} from '../../../../../components';

import { getLinkAriaCurrent } from '../../currentNavItem';

import css from './LinksMenu.module.css';

// LinksMenu is mounted only after hydration (CustomLinksMenu), so the "More" label is measured before
// the browser paints: a menu remounted when the role resolves can then group its links in its first
// frame (backlog #4 C9). The server renders no effects and keeps useEffect (no warning there).
const useLayoutEffectOnClient = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * Link components to be shown inside the dropdown.
 *
 * @param {*} props contain keys: linkConfig, currentNav
 * @returns NamedLink or ExternalLink
 */
const LinkComponent = ({ linkConfig, currentNav }) => {
  const { text, type, href, route, secondaryCta } = linkConfig;
  const ariaCurrent = getLinkAriaCurrent(linkConfig, currentNav);

  const labelClasses = classNames(css.menuLinkLabel, {
    [css.secondaryCtaLabel]: secondaryCta,
  });

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.menuLink, { [css.currentPage]: ariaCurrent });
    return (
      <NamedLink
        name={name}
        params={params}
        to={to}
        className={className}
        aria-current={ariaCurrent}
      >
        <span className={css.menuItemBorder} />
        <span className={labelClasses}>{text}</span>
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.menuLink}>
      <span className={css.menuItemBorder} />
      <span className={labelClasses}>{text}</span>
    </ExternalLink>
  );
};

/**
 * When the links menu shows "More" label (instead of "Menu"), the label width needs to be measured.
 * Both label states are measured: labels[0] is "More" («Ещё»), labels[1] is "Menu" («Меню»).
 *
 * @param {*} props containing: width, setWidth, setAllWidth, labels
 * @returns div with same styles as the real "More" label or null if width is known.
 */
const MeasureMoreMenu = props => {
  const moreMenuRef = useRef(null);
  const [mounted, setMounted] = useState(false);
  const { width, setWidth, setAllWidth, labels } = props;

  useLayoutEffectOnClient(() => {
    setMounted(true);
  }, []);

  useLayoutEffectOnClient(() => {
    if (mounted && moreMenuRef.current && !width) {
      // Widths include padding and the arrow icon. The grouping reserves the "More" width:
      // it is the label that renders whenever some links stay visible. When all links collapse,
      // the "Menu" label renders instead, but then there is no link left to place, so its width
      // does not affect grouping (it is kept for the dropdown placement only).
      const [moreNode, allNode] = Array.from(moreMenuRef.current.childNodes);
      const moreWidth = moreNode?.offsetWidth || 0;
      const allWidth = allNode?.offsetWidth || 0;
      if (moreWidth > 0) {
        setAllWidth?.(allWidth);
        setWidth(moreWidth);
      }
    }
  }, [mounted, moreMenuRef, width]);

  // Component is measured outside of the viewport
  const styleWrapper = !!width
    ? {}
    : {
        style: {
          position: 'absolute',
          top: '-2000px',
          left: '-2000px',
          width: 'auto', // The content defines width
          height: 'var(--topbarHeightDesktop)',
          display: 'flex',
          flexDirection: 'row',
        },
      };

  return !width && mounted
    ? ReactDOM.createPortal(
        <div id="measureMoreLabel" ref={moreMenuRef} {...styleWrapper}>
          {labels.map((label, index) => (
            <div key={index} className={css.linkMenuLabel}>
              {label}
            </div>
          ))}
        </div>,
        document.body
      )
    : null;
};

/**
 * Menu label has text (Menu vs More) and arrow up vs down
 *
 * @param {*} props contain keys: showMoreLabel, isOpen, intl
 * @returns span containing menu label text and IconArrowHead
 */
const MenuLabelContent = ({ showMoreLabel, isOpen, intl }) => {
  const menuLabel = showMoreLabel
    ? intl.formatMessage({ id: 'TopbarDesktop.LinksMenu.more' })
    : intl.formatMessage({ id: 'TopbarDesktop.LinksMenu.all' });
  return (
    <span className={css.linkMenuLabelWrapper}>
      {menuLabel}
      <IconArrowHead
        direction="down"
        size="small"
        rootClassName={css.arrowIcon}
        ariaLabel={menuLabel}
      />
    </span>
  );
};

/**
 * Menu that shows custom links with label showing either "Menu" or "More".
 * The component also measures the width of the "More" label.
 *
 * @param {*} props contain: id, currentNav, links, showMoreLabel, moreLabelWidth, setMoreLabelWidth, intl
 * @returns menu component
 */
const LinksMenu = props => {
  const [isOpen, setIsOpen] = useState(false);
  const { id, currentNav, links, showMoreLabel, moreLabelWidth, setMoreLabelWidth, intl } = props;
  const [allLabelWidth, setAllLabelWidth] = useState(0);
  // Same placement as before: half of the wider label state.
  const widestLabelWidth = Math.max(moreLabelWidth || 0, allLabelWidth);
  const contentPlacementOffset = widestLabelWidth ? -1 * (widestLabelWidth / 2) : 24;
  // The visible trigger is only rendered once some links actually overflow.
  // MeasureMoreMenu is always rendered (off-screen) so moreLabelWidth can be
  // reported even before any link is grouped, which bootstraps grouping.
  const hasLinks = links.length > 0;
  // Q4 (backlog #9): «Ещё» is underlined when the open page is inside it (the item carries aria-current).
  const hasCurrentLink = links.some(linkConfig => getLinkAriaCurrent(linkConfig, currentNav));
  return (
    <>
      {hasLinks ? (
        <Menu
          id={id}
          contentPlacementOffset={contentPlacementOffset}
          contentPosition="left"
          isOpen={isOpen}
          onToggleActive={setIsOpen}
          skipFocusOnNavigation={true}
        >
          <MenuLabel
            id="links-menu-label"
            className={classNames(css.linkMenuLabel, { [css.currentLink]: hasCurrentLink })}
            isOpenClassName={css.linkMenuIsOpen}
          >
            <MenuLabelContent showMoreLabel={showMoreLabel} isOpen={isOpen} intl={intl} />
          </MenuLabel>
          <MenuContent className={css.linkMenuContent}>
            {links.map((linkConfig, index) => {
              return (
                <MenuItem key={`${linkConfig.text}_${index}`}>
                  <LinkComponent linkConfig={linkConfig} currentNav={currentNav} />
                </MenuItem>
              );
            })}
          </MenuContent>
        </Menu>
      ) : null}
      <MeasureMoreMenu
        width={moreLabelWidth}
        setWidth={setMoreLabelWidth}
        setAllWidth={setAllLabelWidth}
        labels={[
          <MenuLabelContent key="more" showMoreLabel={true} intl={intl} />,
          <MenuLabelContent key="all" showMoreLabel={false} intl={intl} />,
        ]}
      />
    </>
  );
};

export default LinksMenu;
